import { FocusEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Ant';

interface ShowMoreToggleProps {
  expanded: boolean;
  /** Id of the text this toggle shows in full. */
  controls: string;
  onToggle: () => void;
  /** Only keyboard focus is reported as focused: a mouse click focuses the button too. */
  onKeyboardFocusChange: (isKeyboardFocused: boolean) => void;
  'data-testid'?: string;
}

/** The more/less link that expands a clamped text in place and collapses it again. */
const ShowMoreToggle = ({
  expanded,
  controls,
  onToggle,
  onKeyboardFocusChange,
  'data-testid': testId,
}: Readonly<ShowMoreToggleProps>) => {
  const { t } = useTranslation();

  /** The toggle sits inside selectable cards, which must not read its click as a selection. */
  const toggle = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onToggle();
  };

  const reportKeyboardFocus = (event: FocusEvent<HTMLElement>) =>
    onKeyboardFocusChange(event.currentTarget.matches(':focus-visible'));

  return (
    <Button
      type="text"
      className="text-cta show-more"
      aria-expanded={expanded}
      aria-controls={controls}
      onClick={toggle}
      onFocus={reportKeyboardFocus}
      onBlur={() => onKeyboardFocusChange(false)}
      data-testid={testId}
    >
      {t(expanded ? 'common:less' : 'common:more')}
    </Button>
  );
};

export default ShowMoreToggle;
